/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    object, bool, func, string,
} from 'prop-types';
import loadable from '@loadable/component';
import { makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Button from '@material-ui/core/Button';
import { useSelector } from 'react-redux';
import QasSuggestionList from '../QasSuggestionList';
import Select from '../../../../../../EnterpriseDesignSystem/Select/Select';
import { getUserSubmittedProductFilterZipcode } from '../../../../../../../../state/ducks/Common/Common-Selectors';

const AddressOrZipInput = loadable(() => import(/* webpackChunkName: "AddressOrZipInput" */ '../../../../../../EnterpriseDesignSystem/Inputs/AddressOrZipInput/AddressOrZipInput'));
const StyledButton = loadable(() => import(/* webpackChunkName: "StyledButton" */ '../../../../../../EnterpriseDesignSystem/Button/StyledButton'));

const useStyles = makeStyles((theme) => ({
    guidedNavEds: {
        width: '100%',
        display: 'flex',
        position: 'relative',
        backgroundColor: '#1c5356',
        padding: '28px 12px',
        alignItems: 'center',
        justifyContent: 'center',
        '@media only screen and (max-width: 1060px)': {
            flexWrap: 'wrap',
        },
        '@media only screen and (max-width: 600px)': {
            padding: '16px 12px',
        },
    },
    guidedNavEdsHeader: {
        textAlign: 'center',
        fontSize: '24px',
        lineHeight: '23px',
        color: '#FFF',
        letterSpacing: '0.18px',
        marginRight: '20px',
        '@media only screen and (max-width: 1060px)': {
            width: '100%',
            marginBottom: '16px',
        },
        '@media only screen and (max-width: 600px)': {
            marginBottom: '12px',
            marginRight: '12px',
            fontSize: '16px',
            lineHeight: '21px',
        },
    },
    guidedNavEdsInputWidthContainer: {
        width: '220px',
        marginRight: '20px',
        position: 'relative',
        '@media only screen and (max-width: 630px)': {
            width: '210px',
            marginRight: '12px',
        },
        '@media only screen and (max-width: 600px)': {
            minWidth: '147px',
            marginRight: '12px',
            width: 'calc(43% - 12px)',
        },
        '@media only screen and (max-width: 420px)': {
            width: '48%',
        },
    },
    guidedNavEdsError: {
        width: '100%',
        lineHeight: '14px',
        borderRadius: '3px',
        background: 'rgba(255, 255, 255, 0.85)',
        padding: '2px 12px',
        marginTop: '2px',
        fontSize: '10px',
        color: '#C80000',
        position: 'absolute',
        top: '100%',
        left: 0,
        '@media only screen and (max-width: 600px)': {
            fontSize: '12px',
            lineHeight: '17px',
            whiteSpace: 'nowrap',
            width: 'auto',
        },
    },
    guidedNavEdsButtonWidthContainer: {
        '@media only screen and (max-width: 600px) and (min-width: 420px)': {
            width: '13%',
        },
        '& button': {
            '@media only screen and (max-width: 600px)': {
                padding: '0px 24px',
            },
        },
        '@media only screen and (max-width: 420px)': {
            marginTop: '12px',
            width: '100%',
        },
    },
    guidedNavEdsSelectWidthContainer: {
        width: '220px',
        marginRight: '20px',
        '@media only screen and (max-width: 630px)': {
            width: '210px',
            marginRight: '12px',
        },
        '@media only screen and (max-width: 600px)': {
            minWidth: '147px',
            marginRight: '12px',
            width: 'calc(43% - 12px)',
        },
        '@media only screen and (max-width: 420px)': {
            marginRight: '0px',
            width: '48%',
        },
    },
    deliveryMessage: {
        color: '#ffffff',
        fontSize: '20px',
        lineHeight: '25px',
        display: 'flex',
        alignItems: 'center',
        fontWeight: 700,
        '@media only screen and (max-width: 600px)': {
            fontSize: '16px',
        },
    },
    collapsedNavEds: {
        padding: '12px 0px',
        '@media only screen and (max-width: 600px)': {
            padding: '8px 0px',
        },
    },
    validZipCode: {
        textDecoration: 'underline',
        color: '#ffffff',
        fontSize: '20px',
        lineHeight: '25px',
        fontWeight: 700,
        cursor: 'pointer',
        marginTop: '0px !important',
        '@media only screen and (max-width: 600px)': {
            fontSize: '16px',
            padding: '0px',
        },
    },
    errorGuidedNavEds: {
        '@media only screen and (max-width: 600px) and (min-width:420.5px)': {
            paddingBottom: '30px',
        },
        '& button': {
            '@media only screen and (max-width: 420px)': {
                marginTop: '18px',
            },
        },
    },
}));

const locations = ['Residence', 'Business', 'Funeral home', 'Hospital', 'Apartment', 'School', 'Church'];

const GuidedNavForCollection = React.forwardRef(
    (
        {
            guidedNavFormData,
            addressFieldOnFocus,
            addressFieldOnBlur,
            handleAddressChange,
            handleLocationFormChange,
            handleButtonSubmit,
            getAddressInformation,
            addressFieldOnClick,
            currentAddress,
            onSubmitCheck,
            guidedNavModalIsOpen,
            qasSuggestions,
            showQasModel,
            hideQasModel,
            handleKeyDown,
            collapsedGnav,
            handleStartOverClick,
            zipValid,
        },
        innerRef,
    ) => {
        const isMobile = useMediaQuery('(max-width: 600px)');
        const classes = useStyles();
        const reduxZip = useSelector(getUserSubmittedProductFilterZipcode);
        return (
            <div className={`${collapsedGnav && reduxZip ? classes.collapsedNavEds : ''} ${classes.guidedNavEds} ${onSubmitCheck ? classes.errorGuidedNavEds : ''}`} style={{ backgroundColor: guidedNavFormData?.background_color }}>
                {collapsedGnav && reduxZip
                    ? (
                        <span className={classes.deliveryMessage}>
                            Delivering to{' '}
                            <Button
                                type="button"
                                onClick={handleStartOverClick}
                                className={classes.validZipCode}
                                style={{

                                    textDecoration: 'underline',

                                }}

                            >
                                {reduxZip}
                            </Button>
                        </span>
                    )
                    : (
                        <>
                            <h4 className={classes.guidedNavEdsHeader}>
                                Find Available Products
                            </h4>
                            <div className={classes.guidedNavEdsInputWidthContainer}>
                                <AddressOrZipInput
                                    id="data-test-guidedNavZipOrAddressField"
                                    label={isMobile ? 'Address or ZIP*' : 'Delivery Address or ZIP*'}
                                    name="guided-nav"
                                    type="text"
                                    handleChange={handleAddressChange}
                                    handleFocus={addressFieldOnFocus}
                                    handleBlur={addressFieldOnBlur}
                                    handleKeyDown={handleKeyDown}
                                    handleClick={addressFieldOnClick}
                                    value={guidedNavModalIsOpen ? '' : currentAddress}
                                    zipInValid={zipValid === false}
                                    innerRef={innerRef}
                                />
                                {zipValid === false
                                && (
                                    <div className={classes.guidedNavEdsError}>
                                        Enter a valid address or ZIP Code
                                    </div>
                                )}
                                <div id="qas-zindex-wrapper" style={{ position: 'relative', zIndex: '1000' }}>
                                    {showQasModel && qasSuggestions && currentAddress?.length > 0 && currentAddress && <QasSuggestionList handleClickAway={hideQasModel} showModel={showQasModel} handlePickAnAddress={getAddressInformation} suggestions={qasSuggestions} />}
                                </div>
                            </div>
                            <div className={classes.guidedNavEdsSelectWidthContainer}>
                                <Select
                                    label="Location"
                                    id="data-test-guidedNavLocation"
                                    dataTestId="gnav-uncollapsed-location-dropdown"
                                    name="occasion"
                                    dropDownList={locations}
                                    handleChange={(e) => {
                                        handleLocationFormChange(e.target.value);
                                    }}
                                />
                            </div>
                            <div className={classes.guidedNavEdsButtonWidthContainer}>
                                <StyledButton
                                    StyledButton
                                    label={isMobile ? 'Go' : 'Submit'}
                                    variant="light"
                                    size="lg"
                                    type="button"
                                    fullWidth
                                    attributes={{
                                        style: {
                                            border: '1px solid #e8f3ee',
                                            backgroundColor: '#e8f3ee',
                                            height: '56px',
                                        },
                                        id: 'data-test-eds-guidedNavSubmitButton',
                                    }}
                                    handleOnClick={() => handleButtonSubmit(false)}
                                />
                            </div>
                        </>
                    )}
            </div>
        );
    },
);

GuidedNavForCollection.propTypes = {
    guidedNavFormData: object.isRequired,
    addressFieldOnFocus: func.isRequired,
    addressFieldOnBlur: func.isRequired,
    handleAddressChange: func.isRequired,
    handleLocationFormChange: func.isRequired,
    handleButtonSubmit: func.isRequired,
    getAddressInformation: func.isRequired,
    addressFieldOnClick: func.isRequired,
    currentAddress: string.isRequired,
    onSubmitCheck: bool.isRequired,
    guidedNavModalIsOpen: bool.isRequired,
    qasSuggestions: object,
    showQasModel: bool,
    hideQasModel: func,
    handleKeyDown: func.isRequired,
    collapsedGnav: bool,
    handleStartOverClick: func,
    zipValid: bool,
};

GuidedNavForCollection.defaultProps = {
    qasSuggestions: {},
    showQasModel: false,
    hideQasModel: () => {},
    collapsedGnav: false,
    handleStartOverClick: () => {},
    zipValid: null,
};

export default GuidedNavForCollection;
